import { useEffect, useState } from 'react';
import configs from '../configs';

const { contractAddrs } = configs;

// LUNC and USTC are listed, so their USD price comes from a market API.
// CoinGecko sends `access-control-allow-origin: *`, so the browser can call it
// directly -- unlike the CoinMarketCap proxy in setupProxy.js, which only
// exists in the CRA dev server and 404s on a deployed static build.
const COINGECKO_URL =
    'https://api.coingecko.com/api/v3/simple/price?ids=terra-luna,terrausd&vs_currencies=usd';

const REFRESH_MS = 60_000;

// DFC has no exchange listing; its price only exists as a ratio in the dfswap
// AMM pool. The pool address is deliberately NOT hardcoded: it is resolved
// from the staking contract (get_config().lp_address -> the LP token's minter
// -> the pair). Hardcoding is how the chart links ended up pointing at a
// drained pool whose price was off by 2.5x.
let pairPromise = null;

function resolveDfcPair(lcd) {
    if (!pairPromise) {
        pairPromise = (async () => {
            const config = await lcd.wasm.contractQuery(contractAddrs.stakelp, { get_config: {} });
            const { minter } = await lcd.wasm.contractQuery(config.lp_address, { minter: {} });
            return minter;
        })().catch(err => {
            pairPromise = null; // let the next tick retry
            throw err;
        });
    }
    return pairPromise;
}

async function readPool(lcd) {
    const pair = await resolveDfcPair(lcd);
    const pool = await lcd.wasm.contractQuery(pair, { pool: {} });
    const dfc = Number(pool.assets.find(a => a.info.token)?.amount);
    const lunc = Number(pool.assets.find(a => a.info.native_token)?.amount);
    const share = Number(pool.total_share);
    if (!(dfc > 0) || !(lunc > 0)) return null;
    return {
        luncPerDfc: lunc / dfc,
        // Both sides of an xyk pool are worth the same, so the pool is worth
        // 2x the LUNC leg. Micro-units cancel in the ratio.
        luncPerLp: share > 0 ? (2 * lunc) / share : null,
    };
}

/**
 * Live USD prices, shared by every consumer.
 *
 * The ticker in the header and each page all want the same numbers. They
 * subscribe to one module-level store rather than each running their own
 * interval, so CoinGecko sees one request a minute no matter how many
 * components are mounted, and a page that mounts mid-interval renders the
 * cached prices immediately instead of flashing em dashes.
 */
let snapshot = { lunc: null, ustc: null, dfc: null, lp: null };
const listeners = new Set();
let timer = null;

async function refresh(lcd) {
    // The two legs are independent on purpose: a rate-limited CoinGecko must
    // not also blank the on-chain figures, and vice versa.
    const [fiat, pool] = await Promise.all([
        fetch(COINGECKO_URL)
            .then(r => (r.ok ? r.json() : null))
            .catch(() => null),
        readPool(lcd).catch(() => null),
    ]);

    const lunc = fiat?.['terra-luna']?.usd ?? null;
    const ustc = fiat?.['terrausd']?.usd ?? null;

    snapshot = {
        lunc,
        ustc,
        dfc: lunc != null && pool ? lunc * pool.luncPerDfc : null,
        lp: lunc != null && pool?.luncPerLp != null ? lunc * pool.luncPerLp : null,
    };
    listeners.forEach(notify => notify(snapshot));
}

/**
 * @returns {{lunc: ?number, ustc: ?number, dfc: ?number, lp: ?number}} USD per
 *   whole token (not micro-units); `lp` is USD per 1 LP of the staked pair.
 */
export function usePrices(lcd) {
    const [prices, setPrices] = useState(snapshot);

    useEffect(() => {
        listeners.add(setPrices);
        setPrices(snapshot);

        if (timer === null) {
            refresh(lcd);
            timer = setInterval(() => refresh(lcd), REFRESH_MS);
        }

        return () => {
            listeners.delete(setPrices);
            if (listeners.size === 0 && timer !== null) {
                clearInterval(timer);
                timer = null;
            }
        };
    }, [lcd]);

    return prices;
}

/** A token price, which needs significant digits rather than 2 decimals. */
export function formatUsdPrice(value) {
    if (value == null || !isFinite(value)) return null;
    if (value >= 1) return '$' + value.toLocaleString('en-US', { maximumFractionDigits: 2 });
    return '$' + value.toPrecision(4).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

/** A position's worth, where 2 decimals is what a reader wants. */
export function formatUsdValue(value) {
    if (value == null || !isFinite(value)) return null;
    if (value === 0) return '$0.00';
    if (value < 0.01) return '<$0.01';
    return '$' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
